import moment from 'moment';

/**
 * Folds a /schedule-new/summary-data response into the four counts the Overview cards
 * show.
 *
 * The endpoint answers in two shapes - an array of per-period buckets (what compiled=true
 * returns over a range) or a single object for one period - so both are handled here
 * rather than at each call site. ScheduleAdmin and ScheduleTeacher both read it, and the
 * two dashboards have to agree on what a count means.
 *
 * `scheduled` is only derived when the response does not state it outright: total minus
 * the three terminal states, floored at 0 so a disagreement between those fields can
 * never render a negative count.
 */
export const summariseOverviewResp = (resp) => {
  let scheduled = 0;
  let ongoing = 0;
  let completed = 0;
  let cancelled = 0;

  if (Array.isArray(resp)) {
    resp.forEach((item) => {
      const itemCompleted = Number(item.completed || 0);
      const itemCancelled = Number(item.cancelled || 0);
      const itemOngoing = Number(item.ongoing || 0);
      const itemTotal = Number(item.total || 0);

      completed += itemCompleted;
      cancelled += itemCancelled;
      ongoing += itemOngoing;

      const itemScheduled = item.scheduled !== undefined ? Number(item.scheduled) : Math.max(0, itemTotal - itemCompleted - itemCancelled - itemOngoing);
      scheduled += itemScheduled;
    });
  } else if (resp && typeof resp === 'object') {
    completed = Number(resp.completed || 0);
    cancelled = Number(resp.cancelled || 0);
    ongoing = Number(resp.ongoing || 0);
    scheduled = resp.scheduled !== undefined ? Number(resp.scheduled) : Math.max(0, Number(resp.total || 0) - completed - cancelled - ongoing);
  }
  return { scheduled, ongoing, completed, cancelled };
};

/**
 * The four Overview cards, in display order. Icons and colours live here rather than in
 * either page so the two dashboards cannot drift into different palettes for the same
 * four states.
 */
export const buildOverviewCards = (stats, icons) => [
  { title: 'scheduled', count: stats.scheduled, icon: icons.scheduled, bgColor: '#E8F0FF' },
  { title: 'ongoing', count: stats.ongoing, icon: icons.ongoing, bgColor: '#E6FFF4' },
  { title: 'completed', count: stats.completed, icon: icons.completed, bgColor: '#F0F0F0' },
  { title: 'cancelled', count: stats.cancelled, icon: icons.cancelled, bgColor: '#FFECEC' },
];

/**
 * The week bucket inside a month. Buckets are fixed calendar slices - 1-7, 8-14, 15-21,
 * 22-28, 29-to-month-end - not rolling sevens, so "this week" means the same dates
 * wherever it is asked for. Lands on the bucket holding today when the target month is
 * the current one, and on the month's first bucket otherwise.
 */
export const weekRangeFor = (year, month) => {
  const isCurrentMonth = year === moment().year() && month === moment().month();
  const day = isCurrentMonth ? moment().date() : 1;
  const at = (d) => moment().year(year).month(month).date(d).startOf('day');
  if (day < 8) return [at(1), at(7).endOf('day')];
  if (day < 15) return [at(8), at(14).endOf('day')];
  if (day < 22) return [at(15), at(21).endOf('day')];
  if (day < 29) return [at(22), at(28).endOf('day')];
  return [at(29), at(29).add(6, 'days').endOf('day')];
};

/**
 * The range the Summary graph should cover after the year or month picker moves.
 * `changed` says which one it was: in the Months view a year change means the whole
 * year, while a month change means that single month.
 */
export const summaryRangeFor = (view, year, month, changed) => {
  if (view === 'month') {
    return changed === 'year'
      ? [moment().year(year).startOf('year'), moment().year(year).endOf('year')]
      : [moment().year(year).month(month).startOf('month'), moment().year(year).month(month).endOf('month')];
  }
  if (view === 'week') return weekRangeFor(year, month);
  const start = moment().year(year).month(month).startOf('week');
  return [start, start.clone().endOf('week')];
};

/**
 * The range to jump to when the Days/Months picker itself changes. Days returns to the
 * current month's live week rather than staying wherever the graph had been scrolled;
 * Months opens the whole of the selected year.
 */
export const rangeForView = (view, year) =>
  view === 'week'
    ? weekRangeFor(moment().year(), moment().month())
    : [moment().year(year).startOf('year'), moment().year(year).endOf('year')];

export default summariseOverviewResp;
